import Anthropic from '@anthropic-ai/sdk';
import { AIError, ClaudeRefusalError, SYSTEM_PROMPT } from './types';

export const CLAUDE_MODEL = 'claude-opus-5-5';
export const CLAUDE_EFFORT = 'medium' as const;

export type ClaudeClientFactory = (apiKey: string) => Anthropic;

const defaultFactory: ClaudeClientFactory = apiKey =>
  new Anthropic({ apiKey, dangerouslyAllowBrowser: true });

export const toAIError = (err: unknown): AIError => {
  if (err instanceof AIError) return err;
  if (err instanceof Anthropic.AuthenticationError) {
    return new AIError('claude', 'Claude key is invalid. Check Settings > API Keys.');
  }
  if (err instanceof Anthropic.RateLimitError) {
    return new AIError('claude', 'Claude rate limit reached. Wait a moment and retry.', true);
  }
  if (err instanceof Anthropic.APIConnectionError) {
    return new AIError('claude', 'Could not reach Claude. Check your connection and retry.', true);
  }
  if (err instanceof Anthropic.APIError) {
    const status = err.status ?? 0;
    // err.message is "<status> <raw JSON body>"; prefer the API's own message text
    const body = err.error as { error?: { message?: string }; message?: string } | undefined;
    const detail = body?.error?.message || body?.message || err.message;
    return new AIError('claude', `Claude error ${status}: ${detail}`, status >= 500);
  }
  return new AIError('claude', `Claude error: ${err instanceof Error ? err.message : String(err)}`);
};

export interface ClaudeTextOptions {
  // Lets Claude search the web on Anthropic's side before answering
  webSearch?: boolean;
  // Constrains the reply to this JSON schema (cannot be combined with webSearch)
  jsonSchema?: Record<string, unknown>;
  // An image for Claude to look at, placed before the prompt
  image?: { mediaType: string; data: string };
}

const WEB_SEARCH_MAX_USES = 3;
const MAX_PAUSED_TURNS = 3;
const JSON_MAX_TOKENS = 64000;

export interface ClaudeResearchResult {
  text: string;
  // The address of every page the web search returned or cited, in the order met
  sources: string[];
}

// Reads page addresses out of a reply's content blocks: search results and the citations attached to text.
export const collectSources = (content: unknown): string[] => {
  const urls: string[] = [];
  const add = (item: unknown) => {
    const url = item && typeof item === 'object' ? (item as { url?: unknown }).url : undefined;
    if (typeof url === 'string' && url && urls.indexOf(url) === -1) urls.push(url);
  };

  if (!Array.isArray(content)) return urls;
  content.forEach(block => {
    if (!block || typeof block !== 'object') return;
    const typed = block as { type?: unknown; content?: unknown; citations?: unknown };
    // A failed search has an error object here instead of a list
    if (typed.type === 'web_search_tool_result' && Array.isArray(typed.content)) typed.content.forEach(add);
    if (typed.type === 'text' && Array.isArray(typed.citations)) typed.citations.forEach(add);
  });
  return urls;
};

const runClaude = async (
  prompt: string,
  apiKey: string,
  createClient: ClaudeClientFactory,
  options: ClaudeTextOptions
): Promise<ClaudeResearchResult> => {
  try {
    const client = createClient(apiKey);
    const messages: Anthropic.Beta.BetaMessageParam[] = [{
      role: 'user',
      content: options.image
        ? [
            {
              type: 'image',
              source: {
                type: 'base64',
                media_type: options.image.mediaType as 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp',
                data: options.image.data,
              },
            },
            { type: 'text', text: prompt },
          ]
        : prompt,
    }];

    const params = {
      model: CLAUDE_MODEL,
      max_tokens: 16000,
      betas: ['server-side-fallback-2026-07-01'],
      fallbacks: 'default' as const,
      output_config: {
        effort: CLAUDE_EFFORT,
        ...(options.jsonSchema ? { format: { type: 'json_schema' as const, schema: options.jsonSchema } } : {}),
      },
      system: SYSTEM_PROMPT,
      ...(options.webSearch
        ? { tools: [{ type: 'web_search_20260209' as const, name: 'web_search' as const, max_uses: WEB_SEARCH_MAX_USES }] }
        : {}),
    };

    // A structured plan can be long and thinking counts against the limit, so it is streamed with a larger cap
    const send = () => options.jsonSchema
      ? client.beta.messages.stream({ ...params, messages: [...messages], max_tokens: JSON_MAX_TOKENS }).finalMessage()
      : client.beta.messages.create({ ...params, messages: [...messages] });

    let response = await send();
    const sources = collectSources(response.content);

    // A long server-side search pauses the turn; re-sending the assistant content resumes it
    let pausedTurns = 0;
    while (response.stop_reason === 'pause_turn') {
      if (pausedTurns >= MAX_PAUSED_TURNS) {
        throw new AIError('claude', 'Claude’s web search took too long. Retry.', true);
      }
      pausedTurns++;
      messages.push({ role: 'assistant', content: response.content as Anthropic.Beta.BetaContentBlockParam[] });
      response = await send();
      collectSources(response.content).forEach(url => {
        if (sources.indexOf(url) === -1) sources.push(url);
      });
    }

    if (response.stop_reason === 'refusal') {
      throw new ClaudeRefusalError();
    }
    if (response.stop_reason === 'max_tokens') {
      throw new AIError('claude', 'Claude’s answer was cut off before it finished. Retry.', true);
    }

    let text = '';
    for (const block of response.content) {
      if (block.type === 'text') text += block.text;
    }
    if (!text.trim()) {
      throw new AIError('claude', 'Claude returned an empty answer. Retry.', true);
    }
    return { text, sources };
  } catch (err) {
    throw toAIError(err);
  }
};

export const claudeText = async (
  prompt: string,
  apiKey: string,
  createClient: ClaudeClientFactory = defaultFactory,
  options: ClaudeTextOptions = {}
): Promise<string> => (await runClaude(prompt, apiKey, createClient, options)).text;

// Web research that also reports which pages the search found, so a quoted source can be checked against them.
export const claudeResearch = (
  prompt: string,
  apiKey: string,
  createClient: ClaudeClientFactory = defaultFactory
): Promise<ClaudeResearchResult> => runClaude(prompt, apiKey, createClient, { webSearch: true });

export const testClaudeKey = async (
  apiKey: string,
  createClient: ClaudeClientFactory = defaultFactory
): Promise<void> => {
  try {
    await createClient(apiKey).models.retrieve(CLAUDE_MODEL);
  } catch (err) {
    throw toAIError(err);
  }
};

export const claudeJson = async (
  prompt: string,
  schema: Record<string, unknown>,
  apiKey: string,
  createClient: ClaudeClientFactory = defaultFactory
): Promise<unknown> => {
  const text = await claudeText(prompt, apiKey, createClient, { jsonSchema: schema });
  try {
    return JSON.parse(text);
  } catch {
    throw new AIError('claude', 'Claude returned data in an unexpected format. Retry.', true);
  }
};

export const claudeVision = async (
  image: { mediaType: string; data: string },
  prompt: string,
  schema: Record<string, unknown>,
  apiKey: string,
  createClient: ClaudeClientFactory = defaultFactory
): Promise<unknown> => {
  const text = await claudeText(prompt, apiKey, createClient, { jsonSchema: schema, image });
  try {
    return JSON.parse(text);
  } catch {
    throw new AIError('claude', 'Claude returned data in an unexpected format. Retry.', true);
  }
};
